import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { fetchGetSplitTest } from '@iwoca/lapi-client/edge';
import { Spinner } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import TopUpStatusMessage from './TopUpStatusMessage';
import messages from './TopUpStatusMessages.messages';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { repeatsTopUpEligibilitySplitTestName } from '../../NewOutstandingView.helpers';

export const NotYetTopUpEligible = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));

    const {
        data: branch,
        isLoading: splitTestLoading,
        isError: splitTestError
    } = useQuery({
        queryKey: [
            'fetchGetSplitTest',
            stateKey,
            repeatsTopUpEligibilitySplitTestName
        ],
        queryFn: () =>
            fetchGetSplitTest({
                stateKey,
                experimentName: repeatsTopUpEligibilitySplitTestName
            }),
        select: ({ data }) => data.branch
    });

    if (accountsLoading || splitTestLoading) {
        return (
            <div className="flex justify-center">
                <Spinner size="large" />
            </div>
        );
    }

    if (accountsError || splitTestError) {
        return <FallbackErrorModal />;
    }

    const inFirstRepaymentTest = branch === 'INCLUDE';

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const amountToRepayBeforeTopUp =
        account.topup_eligibility!.amount_to_repay_before_topup;

    let headingText, descriptionText;
    if (inFirstRepaymentTest) {
        headingText = messages.notYetTopupEligibleFirstRepaymentAlertText;
        descriptionText =
            messages.notYetTopupEligibleFirstRepaymentAlertDescription;
    } else {
        headingText = messages.notYetTopupEligibleAlertText;
        descriptionText = messages.notYetTopupEligibleAlertDescription;
    }

    return (
        <TopUpStatusMessage
            heading={
                <>
                    <FormattedMessage
                        {...headingText}
                        values={{
                            amount: (
                                <FormattedCurrency
                                    value={amountToRepayBeforeTopUp}
                                />
                            )
                        }}
                    />
                </>
            }
            description={<FormattedMessage {...descriptionText} />}
            ctaText={<FormattedMessage {...messages.notTopupEligibleCTAText} />}
        />
    );
};
